import { useQuery } from '@tanstack/react-query';
import { ArrowRight, TicketPercent } from 'lucide-react';
import { Link } from 'react-router';

import { cycleMonths } from '@/features/billing/lib/features';
import { formatPlanPrice } from '@/features/marketing/pricing';
import { cn } from '@/lib/cn';
import {
  fetchTodaysDeals,
  type OfferStripStyle,
  type PublicDeal,
} from '@/features/referrals/api/deals';

/** Billing cycles by length, matching `cycle_key` in the referral engine. */
const CYCLE_BY_MONTHS: Record<number, string> = { 1: 'MONTHLY', 3: 'QUARTERLY', 12: 'YEARLY' };

/**
 * The deal the platform owner put in the header strip, if it is live.
 *
 * Shares the deals page's query, so the strip, the pricing page and
 * `/deals` make one request between them.
 */
export function useStripDeal(): PublicDeal | null {
  const deals = useQuery({
    queryKey: ['deals', 'today'],
    queryFn: fetchTodaysDeals,
    staleTime: 5 * 60 * 1000,
  });
  return deals.data?.find((deal) => deal.show_in_strip) ?? null;
}

/**
 * Rupees a per-cycle deal takes off this plan, or null when it takes nothing.
 * Display only: the invoice is priced by the backend.
 */
export function stripDiscountFor(
  deal: PublicDeal | null,
  plan: { billing_interval: string; billing_interval_count?: number | null; price_minor: number },
): number | null {
  if (!deal?.interval_values || plan.price_minor <= 0) return null;
  const months = cycleMonths(plan.billing_interval, plan.billing_interval_count ?? 1);
  const key = CYCLE_BY_MONTHS[months] ?? plan.billing_interval;
  const amount = Number(deal.interval_values[key] ?? 0);
  return amount > 0 ? Math.min(amount, plan.price_minor / 100) : null;
}

type OfferPlan = {
  code: string;
  billing_interval: string;
  billing_interval_count?: number | null;
  price_minor: number;
};

/**
 * "₹1,000 off with DIWALI1000" under a plan the strip's deal discounts.
 *
 * Shared by the pricing page and the subscribe screen's plan chooser, so a
 * merchant sees the same chip on both. `label` words the lead-in for a
 * translated screen; it is handed the formatted amount.
 */
export function OfferLine({
  offer,
  plan,
  label = (amount) => `${amount} off with`,
  className,
}: {
  offer: PublicDeal | null;
  plan: OfferPlan;
  label?: (amount: string) => string;
  className?: string;
}) {
  const amount = stripDiscountFor(offer, plan);
  if (!offer || amount === null) return null;
  return (
    <p
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-md bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700',
        className ?? 'mt-2',
      )}
      data-testid={`offer-line-${plan.code}`}
    >
      {label(formatPlanPrice(Math.round(amount * 100)))}{' '}
      <span className="font-mono tracking-wider">{offer.code}</span>
    </p>
  );
}

/** The strip as it was before the owner could style it. */
export const DEFAULT_STRIP_STYLE: OfferStripStyle = {
  background: '#0b1220',
  scroll: true,
  direction: 'LEFT_TO_RIGHT',
  speed: 'NORMAL',
};

/**
 * Seconds for one loop. A loop moves the track by half its width, which holds
 * four copies of the offer, so these read as a gentle drift, not a ticker.
 */
const LOOP_SECONDS: Record<OfferStripStyle['speed'], number> = {
  SLOW: 80,
  NORMAL: 55,
  FAST: 35,
};

/** Copies of the offer in each half of the moving track. */
const COPIES = 4;

/**
 * White or near-black, whichever reads better on `background`.
 *
 * WCAG relative luminance, compared against the point where both have equal
 * contrast (about 0.18): a saffron or a pale green gets dark text, a maroon or
 * a navy gets white, and the owner never has to pick a pair that clashes.
 */
export function stripTextColour(background: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(background)?.[1];
  if (!hex) return '#ffffff';
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.18 ? '#0b1220' : '#ffffff';
}

/** One copy of the offer: "DIWALI OFFER  ₹1000 off …  Use code DIWALI1000 →". */
function OfferSegment({ deal, showBenefit }: { deal: PublicDeal; showBenefit: boolean }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-x-3 whitespace-nowrap">
      <TicketPercent className="size-4 shrink-0" aria-hidden />
      <span className="font-semibold uppercase">{deal.title}</span>
      {showBenefit && deal.benefit ? <span className="opacity-80">{deal.benefit}</span> : null}
      <span className="inline-flex items-center gap-1.5">
        <span className="opacity-70">Use code</span>
        <span className="rounded border border-dashed border-current px-2 py-0.5 font-mono font-bold tracking-wider">
          {deal.code}
        </span>
        <ArrowRight className="size-3.5" aria-hidden />
      </span>
    </span>
  );
}

/**
 * The strip itself, drawn from a deal and a style. Exported so the plans
 * screen can preview a style before it is saved; `preview` draws it without
 * the link, so clicking the preview does not leave the settings page.
 */
export function OfferStripView({
  deal,
  style,
  preview = false,
}: {
  deal: PublicDeal;
  style: OfferStripStyle;
  preview?: boolean;
}) {
  const colour = stripTextColour(style.background);
  const announced = [deal.title, deal.benefit, `Use code ${deal.code}`].filter(Boolean).join('. ');

  const still = (
    <span
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center',
        // A moving strip falls back to this for visitors who asked for less
        // motion; a still one always draws it.
        style.scroll ? 'hidden motion-reduce:flex' : 'flex',
      )}
      aria-hidden
    >
      <OfferSegment deal={deal} showBenefit={false} />
      {deal.benefit ? <span className="hidden opacity-80 md:inline">{deal.benefit}</span> : null}
    </span>
  );

  // Two identical halves, so moving the track by half its width lands it
  // exactly where it started and the loop has no seam. Left to right plays
  // the same keyframes in reverse.
  const moving = style.scroll ? (
    <span className="offer-strip-viewport block overflow-hidden py-2 motion-reduce:hidden" aria-hidden>
      <span
        className={cn(
          'offer-strip-track flex w-max',
          style.direction === 'LEFT_TO_RIGHT' && 'offer-strip-track--ltr',
        )}
        data-testid="offer-strip-track"
        data-direction={style.direction}
        style={{ animationDuration: `${LOOP_SECONDS[style.speed]}s` }}
      >
        {Array.from({ length: COPIES * 2 }, (_, index) => (
          <span key={index} className="inline-flex shrink-0 items-center pr-12">
            <OfferSegment deal={deal} showBenefit />
          </span>
        ))}
      </span>
    </span>
  ) : null;

  const className =
    'offer-strip block min-h-10 text-xs font-medium tracking-wide hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-[-2px] sm:text-sm';
  const css = { backgroundColor: style.background, color: colour };
  const body = (
    <>
      {/* Read once, in full, whatever the strip is doing on screen. */}
      <span className="sr-only">{announced}</span>
      {moving}
      {still}
    </>
  );

  if (preview) {
    return (
      <div className={className} style={css} data-testid="offer-strip-preview">
        {body}
      </div>
    );
  }
  return (
    <Link
      to={`/pricing?ref=${encodeURIComponent(deal.code)}`}
      data-testid="offer-strip"
      className={className}
      style={css}
    >
      {body}
    </Link>
  );
}

/**
 * The thin offer bar above the marketing header: "Diwali Offer · ₹1000 off a
 * yearly plan · DIWALI1000".
 *
 * Switched on per deal at `/platform/deals`; its colour and motion are set on
 * the plans screen, under Signing up, and arrive with the deal. Renders
 * nothing while loading, on an error, or when no live deal holds the strip,
 * so the header never waits on a promotion. It links to pricing with the code
 * as `?ref=`, which carries the code through sign-up on its own.
 */
export function OfferStrip() {
  const deal = useStripDeal();
  if (!deal) return null;
  return <OfferStripView deal={deal} style={deal.strip_style ?? DEFAULT_STRIP_STYLE} />;
}
